/**
 * Cliente HTTP centralizado del módulo contable (instrucción 61: todas las llamadas pasan por aquí).
 * No guarda tokens: el host de la aplicación entrega cómo obtenerlos con configureAccountingApi().
 * El ID de empresa NUNCA se envía en el cuerpo: la API lo toma del JWT (opcionalmente del encabezado X-Empresa-Id
 * cuando el token trae varias empresas permitidas).
 */
export interface AccountingApiConfig {
  /** URL base de la API, p. ej. "https://servidor/miapi". Vacío = mismo origen. */
  baseUrl: string;
  /** Devuelve el JWT vigente (o null). */
  getToken: () => string | null | Promise<string | null>;
  /** Empresa elegida cuando el usuario tiene varias. null = la del token. */
  getCompanyId?: () => number | null;
  /** Se llama ante 401 (sesión vencida). */
  onUnauthorized?: () => void;
  companyHeader?: string;
}

let config: AccountingApiConfig = { baseUrl: '', getToken: () => null };

export function configureAccountingApi(next: Partial<AccountingApiConfig>): void {
  config = { ...config, ...next };
}

export interface ApiEnvelope<T> {
  success: boolean;
  code?: string | null;
  message?: string | null;
  data?: T | null;
  details?: unknown;
}

/** Error uniforme del módulo (mismo formato que AccountingExceptionFilter). */
export class AccountingApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
  /** Mensajes de validación por campo, si la API los envió (AccountingValidationFilter). */
  get fieldErrors(): Record<string, string[]> {
    const d = this.details as Record<string, unknown> | undefined;
    if (!d || typeof d !== 'object') return {};
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(d)) {
      if (Array.isArray(v)) out[k] = v.map(String);
    }
    return out;
  }
}

export type QueryValue = string | number | boolean | Date | null | undefined;

export function toIsoDate(d: Date | string): string {
  if (typeof d === 'string') return d.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function buildQuery(params?: Record<string, QueryValue>): string {
  if (!params) return '';
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    q.append(k, v instanceof Date ? toIsoDate(v) : String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

async function headers(json: boolean): Promise<Record<string, string>> {
  const h: Record<string, string> = { Accept: 'application/json' };
  if (json) h['Content-Type'] = 'application/json';
  const token = await config.getToken();
  if (token) h.Authorization = `Bearer ${token}`;
  const company = config.getCompanyId?.();
  if (company) h[config.companyHeader ?? 'X-Empresa-Id'] = String(company);
  return h;
}

async function parseError(res: Response): Promise<AccountingApiError> {
  let body: ApiEnvelope<unknown> | null = null;
  try {
    body = (await res.json()) as ApiEnvelope<unknown>;
  } catch {
    body = null;
  }
  if (res.status === 401) config.onUnauthorized?.();
  const fallback =
    res.status === 401 ? 'La sesión expiró. Inicie sesión de nuevo.'
      : res.status === 403 ? 'No tiene permiso para esta operación.'
        : res.status === 404 ? 'El recurso no existe.'
          : `Error ${res.status} en el servidor.`;
  return new AccountingApiError(res.status, body?.code ?? `HTTP_${res.status}`, body?.message ?? fallback, body?.details);
}

export interface RequestResult<T> {
  data: T;
  message?: string | null;
}

export async function request<T>(method: string, path: string, body?: unknown, query?: Record<string, QueryValue>): Promise<RequestResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${config.baseUrl}${path}${buildQuery(query)}`, {
      method,
      headers: await headers(body !== undefined),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new AccountingApiError(0, 'NETWORK_ERROR', 'No se pudo conectar con el servidor.');
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return { data: undefined as T };
  const env = (await res.json()) as ApiEnvelope<T>;
  if (!env.success) throw new AccountingApiError(res.status, env.code ?? 'ERROR', env.message ?? 'Operación rechazada.', env.details);
  return { data: env.data as T, message: env.message };
}

export const http = {
  get: <T>(path: string, query?: Record<string, QueryValue>) => request<T>('GET', path, undefined, query).then((r) => r.data),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}).then((r) => r.data),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}).then((r) => r.data),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}).then((r) => r.data),
  del: <T = null>(path: string) => request<T>('DELETE', path).then((r) => r.data),
};

/** Descarga un CSV generado por la API (format=csv) y lo guarda con el nombre que envía el servidor. */
export async function downloadCsv(path: string, query: Record<string, QueryValue>, fallbackName: string): Promise<void> {
  const res = await fetch(`${config.baseUrl}${path}${buildQuery({ ...query, format: 'csv' })}`, { headers: await headers(false) });
  if (!res.ok) throw await parseError(res);
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const name = match ? decodeURIComponent(match[1]) : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Segmento de URL seguro (códigos de cuenta, asiento, etc.). */
export const seg = (v: string | number) => encodeURIComponent(String(v));

/** Descarga un archivo protegido (con el JWT) y devuelve una URL local para mostrarlo; liberar con URL.revokeObjectURL. */
export async function fetchBlobUrl(path: string): Promise<{ url: string; type: string }> {
  let res: Response;
  try {
    res = await fetch(`${config.baseUrl}${path}`, { headers: { ...(await headers(false)), Accept: '*/*' } });
  } catch {
    throw new AccountingApiError(0, 'NETWORK_ERROR', 'No se pudo conectar con el servidor.');
  }
  if (!res.ok) throw await parseError(res);
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), type: blob.type || res.headers.get('Content-Type') || '' };
}
